// The quantities behind the money screens — arranged so they can be CHECKED.
//
// Job Costs prices subcontract labour from production reports; the Contract tab
// prices earned revenue from installed quantity. Until this module existed
// there was no screen where either number could be verified against the reports
// it came from — the owner trusted a figure he could not see. Everything here
// exists to make that figure auditable.
//
// Pure arithmetic on plain objects: no DOM, no network, no Supabase.
//
// THE INVARIANT: the per-item project total this module reports is not a second
// opinion about installed quantity — it IS `buildInstalledMap`, the same
// function 66-jobcosts.js and 68-jobvalue.js already call, read back out. Those
// two files drifted apart once on exactly this question (one skipped rows with
// a null material_type_id, the other did not); a third independent sum would be
// the next drift, and it would be the one that silently made an AUDIT screen
// disagree with the thing it audits. So this module never sums `quantity`
// itself. It calls buildInstalledMap and reports what came back.

import { buildInstalledMap } from './job-value.js';

/**
 * Why a quantity that exists in the database does not reach the Contract tab.
 *
 * A screen built to check numbers has to be loudest exactly where the numbers
 * do NOT line up — a discrepancy rendered as a silent zero is worse than no
 * screen at all, because it launders the gap into an apparent agreement.
 */
export const NO_ITEM      = 'no-item';       // line carries no material type: dropped by buildInstalledMap
export const UNKNOWN_ITEM = 'unknown-item';  // counted in the roll-up, but the item is not in this org's catalogue
export const LEGACY_ONLY  = 'legacy-only';   // report has no line items at all; only retired columns carry its quantity

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

/** YYYY-MM from a YYYY-MM-DD date column. Used only to group, never to compute. */
export function monthKey(dateStr) {
  return typeof dateStr === 'string' && dateStr.length >= 7 ? dateStr.slice(0, 7) : '';
}

/**
 * Does this report carry quantity ONLY in the retired per-trade columns?
 *
 * `blocks_installed` / `bricks_installed` / `firestop_lf` predate line items.
 * A report like this still feeds Job Costs' subcontract labour maths, so it is
 * real production and real money — but it contributes nothing to the contract,
 * because there is no material type on it to earn against. That asymmetry is
 * precisely the kind of thing this screen exists to show.
 *
 * The column NAMES never reach the interface. They are masonry vocabulary on a
 * product that ships to twelve trades, and this repo has repeatedly had to
 * sweep exactly that kind of leak back out (docs/prompts/2026-07-30-masonry-
 * residue-sweep.md). The screen says "quantities in a retired format" and links
 * the PDF, which has the real detail; it never invents a heading.
 *
 * Trade Mark has zero of these today. It is built for anyway: other tenants and
 * future data will hit it, and a silent zero is how this bug ships.
 */
export function isLegacyOnly(report, lineCount) {
  if (lineCount > 0) return false;
  return num(report?.blocks_installed) > 0
      || num(report?.bricks_installed) > 0
      || num(report?.firestop_lf)      > 0;
}

/**
 * Per-item project totals, in the order the catalogue lists them (named items
 * first, alphabetically; unidentified ones last so a discrepancy sorts to
 * where it gets read).
 *
 * `quantity` is read straight out of buildInstalledMap — see the header. The
 * only thing walked separately here is `reportCount`, which is a COUNT of
 * reports, not a sum of quantity, so it cannot drift the invariant.
 *
 * `known: false` marks an item counted by the roll-up whose material type is
 * not in this org's catalogue (deleted, or belonging to another tenant — the
 * FK on production_line_items.material_type_id does not check org). It still
 * adds to installed quantity, so it is shown with its real number; but no
 * contract line can be written against an item that is not in the catalogue,
 * so it will never earn. Flagged, never dropped.
 */
export function itemTotals(lineItems, typeById) {
  const installed = buildInstalledMap(lineItems);       // ← the shared roll-up. Never re-summed here.
  const types = typeById || {};

  const reportsPerType = {};
  (lineItems || []).forEach(r => {
    if (!r.material_type_id) return;
    (reportsPerType[r.material_type_id] = reportsPerType[r.material_type_id] || new Set())
      .add(r.production_report_id);
  });

  const rows = Object.keys(installed).map(id => {
    const t = types[id];
    return {
      materialTypeId: id,
      name: t ? t.name : null,
      unit: t ? t.unit : null,
      quantity: installed[id],
      reportCount: reportsPerType[id] ? reportsPerType[id].size : 0,
      known: !!t,
    };
  });

  rows.sort((a, b) => {
    if (a.known !== b.known) return a.known ? -1 : 1;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
  return rows;
}

/**
 * Quantity present in the data that buildInstalledMap threw away — a line with
 * no material type at all. It cannot earn, and the Contract tab will never
 * mention it.
 *
 * `production_line_items.material_type_id` is NOT NULL in the live schema, so
 * today this is unreachable through the database and returns []. It is still
 * computed, because buildInstalledMap defends against the case and an audit
 * screen that assumed the constraint would go quiet the moment a migration
 * relaxed it — which is the only way anyone would ever notice, too late.
 */
export function uncountedLines(lineItems) {
  return (lineItems || [])
    .filter(r => !r.material_type_id)
    .map(r => ({
      id: r.id,
      reportId: r.production_report_id,
      quantity: num(r.quantity),
      reason: NO_ITEM,
    }));
}

/**
 * The reports themselves, newest first, each carrying its own quantities and
 * the project-to-date running total for every item on it.
 *
 * The running total is accumulated in DATE order (oldest first) and then the
 * list is reversed for display, so the newest report's running total is the
 * whole project total — the same figure the summary above it shows, arrived at
 * by a different route and printed right next to the rows that produced it.
 * That side-by-side is the check: if the daily numbers did not add up to the
 * headline, the top row would say so.
 *
 * Ties on report_date are broken by id so the running total is deterministic
 * rather than dependent on the order Postgres happened to return rows in — two
 * crews filing the same day is normal (Trade Mark has projects with two).
 */
export function buildReportRows({ reports, lineItems, typeById, profileById }) {
  const types = typeById || {};
  const profiles = profileById || {};

  const linesByReport = {};
  (lineItems || []).forEach(r => {
    (linesByReport[r.production_report_id] = linesByReport[r.production_report_id] || []).push(r);
  });

  const chronological = (reports || []).slice().sort((a, b) => {
    const d = String(a.report_date || '').localeCompare(String(b.report_date || ''));
    return d !== 0 ? d : String(a.id || '').localeCompare(String(b.id || ''));
  });

  const running = {};
  const rows = chronological.map(rep => {
    const lines = linesByReport[rep.id] || [];
    const items = [];
    let uncounted = 0;

    lines.forEach(l => {
      const qty = num(l.quantity);
      if (!l.material_type_id) { uncounted += qty; return; }
      running[l.material_type_id] = (running[l.material_type_id] || 0) + qty;
      const t = types[l.material_type_id];
      items.push({
        materialTypeId: l.material_type_id,
        name: t ? t.name : null,
        unit: t ? t.unit : null,
        quantity: qty,
        runningTotal: running[l.material_type_id],
        known: !!t,
      });
    });
    items.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

    // foreman_name is denormalized onto the report at submit time; the profile
    // is the live name. Prefer the profile so a renamed crew reads correctly
    // across its whole history, and fall back to the stored name for a profile
    // that has since been removed — never render an empty who on a screen whose
    // whole job is "who reported this".
    const prof = rep.foreman_id ? profiles[rep.foreman_id] : null;
    return {
      id: rep.id,
      date: rep.report_date,
      month: monthKey(rep.report_date),
      who: (prof && prof.full_name) || rep.foreman_name || null,
      isSubcontractor: !!(prof && prof.is_subcontractor),
      submissionId: rep.submission_id || null,
      notes: rep.notes || '',
      weather: rep.weather || '',
      items,
      uncountedQty: uncounted,
      legacyOnly: isLegacyOnly(rep, lines.length),
      empty: lines.length === 0 && !isLegacyOnly(rep, lines.length),
    };
  });

  return rows.reverse();
}

/** Reports bucketed into month groups, newest month first, rows already newest-first. */
export function groupByMonth(reportRows) {
  const groups = [];
  const byKey = {};
  (reportRows || []).forEach(r => {
    if (!byKey[r.month]) { byKey[r.month] = { month: r.month, rows: [] }; groups.push(byKey[r.month]); }
    byKey[r.month].rows.push(r);
  });
  return groups;
}

/**
 * Everything the tab renders for one project, in one call, so the summary and
 * the rows below it can never come from two different reads of the same data.
 */
export function projectProduction({ reports, lineItems, typeById, profileById }) {
  const rows = buildReportRows({ reports, lineItems, typeById, profileById });
  const items = itemTotals(lineItems, typeById);
  const uncounted = uncountedLines(lineItems);

  const legacyOnly = rows.filter(r => r.legacyOnly);
  const emptyReports = rows.filter(r => r.empty);
  const unknownItems = items.filter(i => !i.known);
  const dates = rows.map(r => r.date).filter(Boolean).sort();
  // A row is one crew's day. The foreman files ONE Production Report form for
  // several days, so "reports" counts forms (a row with no form counts alone)
  // and "days" counts calendar days — "17 reports · 53 days" (T8).
  const daysByType = {};
  rows.forEach(r => r.items.forEach(i => {
    (daysByType[i.materialTypeId] = daysByType[i.materialTypeId] || new Set()).add(r.date);
  }));
  items.forEach(i => { i.dayCount = daysByType[i.materialTypeId] ? daysByType[i.materialTypeId].size : 0; });

  return {
    items, rows, uncounted,
    groups: groupByMonth(rows),
    reportCount: rows.length,
    filedCount: new Set(rows.map(r => r.submissionId || r.id)).size,
    dayCount: new Set(dates).size,
    firstDate: dates[0] || null,
    lastDate: dates[dates.length - 1] || null,
    crewCount: new Set(rows.map(r => r.who).filter(Boolean)).size,
    legacyOnly, emptyReports, unknownItems,
    // One flag for "the numbers on this screen and the numbers the Contract
    // tab earns from are not the same set" — the single fact an auditing
    // screen must never leave the reader to work out for themselves.
    hasDiscrepancy: uncounted.length > 0 || legacyOnly.length > 0
                 || unknownItems.length > 0 || emptyReports.length > 0,
  };
}
